import { Label } from '@/components/ui/label';
import LabelMultiSelect from '@/components/tickets/LabelMultiSelect';

/**
 * Select de catálogo con opción huérfana si el valor actual no está en la lista.
 */
export function CatalogSelect({
  id,
  label,
  value,
  onChange,
  items = [],
  placeholder = '—',
  allowEmpty = true,
  disabled = false,
  className = 'w-full rounded-sm border border-brand-border bg-white px-2 py-1.5 text-xs',
  testId,
}) {
  const ids = new Set((items || []).map((i) => i.id));
  const orphan = value && !ids.has(value);

  return (
    <div className="space-y-1">
      {label ? (
        <Label htmlFor={id} className="text-[10px] uppercase tracking-wider text-zinc-500">
          {label}
        </Label>
      ) : null}
      <select
        id={id}
        data-testid={testId}
        className={className}
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || '')}
      >
        {allowEmpty ? <option value="">{placeholder}</option> : null}
        {orphan ? (
          <option value={value}>
            {value} (no está en catálogo)
          </option>
        ) : null}
        {(items || []).map((item) => (
          <option key={item.id} value={item.id}>
            {item.name || item.id}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Formulario compartido de metadata de routing (Monitor / Tickets / filtros).
 * Persiste ids; muestra names.
 */
export default function RoutingMetadataForm({
  value = {},
  onChange,
  catalogs = {},
  show = {
    intent: true,
    category: true,
    label: true,
    labels: false,
    priority: true,
    requesting_department: true,
  },
  disabled = false,
  dense = false,
}) {
  const set = (key, v) => onChange({ ...value, [key]: v });
  const grid = dense ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-3';

  return (
    <div className={grid} data-testid="routing-metadata-form">
      {show.intent ? (
        <CatalogSelect
          id="rm-intent"
          label="Intent"
          value={value.intent || ''}
          onChange={(v) => set('intent', v)}
          items={catalogs.intents}
          placeholder="Sin intent"
          disabled={disabled}
          testId="rm-intent"
        />
      ) : null}
      {show.category ? (
        <CatalogSelect
          id="rm-category"
          label="Categoría"
          value={value.category || ''}
          onChange={(v) => set('category', v)}
          items={catalogs.categories}
          placeholder="Sin categoría"
          disabled={disabled}
          testId="rm-category"
        />
      ) : null}
      {show.label && !show.labels ? (
        <CatalogSelect
          id="rm-label"
          label="Label"
          value={value.label || ''}
          onChange={(v) => set('label', v)}
          items={catalogs.labels}
          placeholder="Sin label"
          disabled={disabled}
          testId="rm-label"
        />
      ) : null}
      {show.labels ? (
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Labels</Label>
          <LabelMultiSelect
            catalog={catalogs.labels || []}
            value={value.labels || (value.label ? [value.label] : [])}
            onChange={(labels) => onChange({ ...value, labels, label: labels[0] || '' })}
            testId="rm-labels"
          />
        </div>
      ) : null}
      {show.priority ? (
        <CatalogSelect
          id="rm-priority"
          label="Prioridad"
          value={value.priority || ''}
          onChange={(v) => set('priority', v)}
          items={catalogs.priorities}
          placeholder="Sin prioridad"
          allowEmpty={!value.priority}
          disabled={disabled}
          testId="rm-priority"
        />
      ) : null}
      {show.requesting_department ? (
        <CatalogSelect
          id="rm-department"
          label="Gerencia"
          value={value.requesting_department || ''}
          onChange={(v) => set('requesting_department', v)}
          items={catalogs.requesting_departments}
          placeholder="Sin gerencia"
          disabled={disabled}
          testId="rm-department"
        />
      ) : null}
    </div>
  );
}
